import { DriveContact } from '@/models/drive.ts'
import { useTrackStat } from '@/contexts/statsScopeContext.tsx'
import { ReportableStatKind } from '@/models/stats.ts'
import {
  IconMail,
  IconPhone,
  IconWorld,
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandX,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBrandPinterest,
  IconBrandGoogle,
  IconUser,
} from '@tabler/icons-react'

interface ContactCardProps {
  contact: DriveContact
  // 'vertical' = always stacked (for modals), 'responsive' = horizontal on desktop (for footer)
  layout?: 'vertical' | 'responsive'
}

function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '')
}

/** A social channel plus what a click on it is reported as. */
interface SocialLink {
  url: string
  icon: any
  label: string
  /** The website gets its own event kind; everything else is a social channel. */
  kind: Extract<ReportableStatKind, 'contact_click_website' | 'contact_click_social'>
  /** Channel name sent as `target` for social clicks. */
  target?: string
}

function ContactDetails({ contact, socialLinks, showStripe = false }: {
  contact: DriveContact
  socialLinks: SocialLink[]
  showStripe?: boolean
}) {
  const displayName = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
  const track = useTrackStat()

  const contactLinks = [
    contact.email ? { href: `mailto:${contact.email}`, icon: IconMail, label: contact.email, kind: 'contact_click_email' as const } : null,
    contact.phone ? { href: `tel:${contact.phone}`, icon: IconPhone, label: contact.phone, kind: 'contact_click_phone' as const } : null,
  ].filter(Boolean) as Array<{ href: string, icon: any, label: string, kind: ReportableStatKind }>

  return (
    <>
      {/* Name */}
      {displayName && (
        <h3 className='text-3xl font-serif'>{displayName}</h3>
      )}

      {/* Company */}
      {contact.company && (
        <p className='opacity-75 -mt-2'>{contact.company}</p>
      )}

      {/* Primary color stripe — only on vertical/mobile */}
      {showStripe && (displayName || contact.company) && (
        <div className='w-full h-[1px] bg-primary opacity-50' />
      )}

      {/* Contact */}
      {contactLinks.length > 0 && (
        <div className='flex flex-col items-start gap-4 mt-4'>
          <h4 className='text-2xl font-serif'>Kontakt</h4>
          {contactLinks.map(link => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => track(link.kind)}
              className='flex items-center gap-3 text-base hover:text-primary transition-colors'
            >
              <link.icon size={28} className='flex-shrink-0' />
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      )}

      {/* Social links */}
      {socialLinks.length > 0 && (
        <div className='flex flex-col items-start gap-4 mt-2'>
          <h4 className='text-2xl font-serif'>Social Media</h4>
          {socialLinks.map(link => (
            <a
              key={link.label}
              href={link.url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => track(link.kind, link.target ? { target: link.target } : undefined)}
              className='flex items-center gap-3 text-base hover:text-primary transition-colors'
            >
              <link.icon size={28} className='flex-shrink-0' />
              <span>{stripProtocol(link.url!)}</span>
            </a>
          ))}
        </div>
      )}
    </>
  )
}

export default function ContactCard({ contact, layout = 'responsive' }: ContactCardProps) {
  const socialLinks = [
    { url: contact.website, icon: IconWorld, label: 'Website', kind: 'contact_click_website' as const },
    { url: contact.instagram, icon: IconBrandInstagram, label: 'Instagram', kind: 'contact_click_social' as const, target: 'instagram' },
    { url: contact.facebook, icon: IconBrandFacebook, label: 'Facebook', kind: 'contact_click_social' as const, target: 'facebook' },
    { url: contact.twitter, icon: IconBrandX, label: 'X', kind: 'contact_click_social' as const, target: 'twitter' },
    { url: contact.tiktok, icon: IconBrandTiktok, label: 'TikTok', kind: 'contact_click_social' as const, target: 'tiktok' },
    { url: contact.youtube, icon: IconBrandYoutube, label: 'YouTube', kind: 'contact_click_social' as const, target: 'youtube' },
    { url: contact.pinterest, icon: IconBrandPinterest, label: 'Pinterest', kind: 'contact_click_social' as const, target: 'pinterest' },
    { url: contact.google, icon: IconBrandGoogle, label: 'Google', kind: 'contact_click_social' as const, target: 'google' },
  ].filter(link => !!link.url) as SocialLink[]

  const imagePlaceholder = (className: string) => (
    <div className={`bg-secondary flex items-center justify-center rounded ${className}`}>
      <IconUser size={48} className='opacity-30' />
    </div>
  )

  const imageOrPlaceholder = (className: string) =>
    contact.image
      ? <img src={contact.image} alt='' className={`rounded object-cover ${className}`} />
      : imagePlaceholder(className)

  // Vertical layout (modal): image full width, content below
  if (layout === 'vertical') {
    return (
      <div className='flex flex-col items-start gap-4'>
        {imageOrPlaceholder('w-full aspect-[3/2]')}
        <ContactDetails contact={contact} socialLinks={socialLinks} showStripe />
      </div>
    )
  }

  // Responsive layout (footer): vertical on mobile, horizontal on desktop
  return (
    <div>
      {/* Mobile: vertical with full-width image */}
      <div className='md:hidden flex flex-col items-start gap-4'>
        {imageOrPlaceholder('w-full aspect-[3/2]')}
        <ContactDetails contact={contact} socialLinks={socialLinks} showStripe />
      </div>

      {/* Desktop: horizontal — image left, content right */}
      <div className='hidden md:flex items-start gap-10'>
        {imageOrPlaceholder('w-64 h-64 flex-shrink-0')}
        <div className='flex flex-col items-start gap-4 flex-1'>
          <ContactDetails contact={contact} socialLinks={socialLinks} />
        </div>
      </div>
    </div>
  )
}
